import React, { useEffect, useRef, useState } from "react";
import { Calender, Search, Transfer } from "../shared/icon";

import axiosInstance from "../api/axiosInstance";
import { isAuthenticated } from "../utils/auth";

const getRangeForDays = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
};

const dateToISOStart = (yyyyMmDd) =>
  yyyyMmDd ? new Date(`${yyyyMmDd}T00:00:00.000Z`).toISOString() : null;
const dateToISOEnd = (yyyyMmDd) =>
  yyyyMmDd ? new Date(`${yyyyMmDd}T23:59:59.999Z`).toISOString() : null;

const UserProfitLossTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [activeFilter, setActiveFilter] = useState("7");
  const [showCalendar, setShowCalendar] = useState(false);
  const [pickerFrom, setPickerFrom] = useState("");
  const [pickerTo, setPickerTo] = useState("");
  const calendarRef = useRef(null);

  const isLoggedIn = typeof window !== "undefined" ? isAuthenticated() : false;

  const displayDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" })
      : "—";

  const fetchPL = async (from, to) => {
    if (!from || !to) return;
    if (!isLoggedIn) return;

    try {
      setLoading(true);
      const payload = { from_date: from, to_date: to };
      const res = await axiosInstance.post(
        "/report/getAffUserSportWisePL",
        payload,
      );
      if (res?.data?.status) {
        const data = res.data?.data;
        setRows(Array.isArray(data) ? data : (data?.response ?? []));
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setRows([]);
      setLoading(false);
      return;
    }

    const { from, to } = getRangeForDays(7);
    setFromDate(from);
    setToDate(to);
    fetchPL(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (!showCalendar) return;
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  const handleQuickFilter = (days) => {
    if (!isLoggedIn) return;
    setActiveFilter(String(days));
    const { from, to } = getRangeForDays(days);
    setFromDate(from);
    setToDate(to);
    fetchPL(from, to);
  };

  const openCalendar = () => {
    if (!isLoggedIn) return;
    setPickerFrom(fromDate ? fromDate.slice(0, 10) : "");
    setPickerTo(toDate ? toDate.slice(0, 10) : "");
    setShowCalendar(true);
  };

  const applyCalendarRange = () => {
    const from = dateToISOStart(pickerFrom);
    const to = dateToISOEnd(pickerTo);
    if (from && to) {
      setFromDate(from);
      setToDate(to);
      setActiveFilter(null);
      setShowCalendar(false);
      fetchPL(from, to);
    }
  };

  const handleSearch = () => {
    if (!isLoggedIn) return;
    if (fromDate && toDate) fetchPL(fromDate, toDate);
  };

  return (
    <>
      <div className="filter-card">
        <div className="filter-inner">
          <div className="date-search-row">
            <div
              className="date-range-box"
              ref={calendarRef}
              style={{ position: "relative" }}
            >
              <div className="date-text">
                <div className="date-values">
                  <span>{fromDate ? displayDate(fromDate) : "—"}</span>
                  <Transfer />
                  <span>{toDate ? displayDate(toDate) : "—"}</span>
                </div>
              </div>
              <Calender
                onClick={openCalendar}
                style={{ cursor: isLoggedIn ? "pointer" : "default" }}
              />

              {showCalendar && (
                <div
                  className="calendar-popover"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    marginTop: "4px",
                    padding: "12px",
                    background: "var(--color-secondary, #1a1a1a)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    minWidth: "260px",
                  }}
                >
                  <label style={{ fontSize: "12px", color: "#fff" }}>
                    From
                    <input
                      type="date"
                      value={pickerFrom}
                      onChange={(e) => setPickerFrom(e.target.value)}
                      style={{
                        display: "block",
                        marginTop: "4px",
                        padding: "6px 8px",
                        width: "100%",
                        background: "#333",
                        border: "1px solid #555",
                        borderRadius: "4px",
                        color: "#fff",
                      }}
                    />
                  </label>
                  <label style={{ fontSize: "12px", color: "#fff" }}>
                    To
                    <input
                      type="date"
                      value={pickerTo}
                      onChange={(e) => setPickerTo(e.target.value)}
                      style={{
                        display: "block",
                        marginTop: "4px",
                        padding: "6px 8px",
                        width: "100%",
                        background: "#333",
                        border: "1px solid #555",
                        borderRadius: "4px",
                        color: "#fff",
                      }}
                    />
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={applyCalendarRange}
                      className="thm-but btn-gradient"
                      style={{ flex: 1 }}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCalendar(false)}
                      style={{
                        padding: "6px 12px",
                        background: "#444",
                        border: "none",
                        borderRadius: "4px",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="icon-btn"
              type="button"
              onClick={handleSearch}
              aria-label="Search"
              disabled={!isLoggedIn}
            >
              <Search />
            </button>
          </div>

          <div className="quick-filters">
            <div
              className={`quick-filter ${activeFilter === "7" ? "active-filter" : ""}`}
              onClick={() => handleQuickFilter(7)}
              onKeyDown={(e) => e.key === "Enter" && handleQuickFilter(7)}
              role="button"
              tabIndex={0}
            >
              Last 7 days
            </div>
            <div
              className={`quick-filter ${activeFilter === "14" ? "active-filter" : ""}`}
              onClick={() => handleQuickFilter(14)}
              onKeyDown={(e) => e.key === "Enter" && handleQuickFilter(14)}
              role="button"
              tabIndex={0}
            >
              Last 14 days
            </div>
            <div
              className={`quick-filter ${activeFilter === "28" ? "active-filter" : ""}`}
              onClick={() => handleQuickFilter(28)}
              onKeyDown={(e) => e.key === "Enter" && handleQuickFilter(28)}
              role="button"
              tabIndex={0}
            >
              Last 28 days
            </div>
          </div>
        </div>
      </div>

      <div className="af-table-wrapper">
        <table className="af-table default-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Event</th>
              <th>Profit / Loss</th>
            </tr>
          </thead>

          <tbody id="userTableBody">
            {loading ? (
              <tr>
                <td colSpan={3} className="no-records">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="no-records">
                  No Records Found
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row._id || row.id || index}>
                  <td>{displayDate(row.date || row.createdAt)}</td>
                  <td>{row.event || row.eventName || row.sport || "-"}</td>
                  <td
                    className={
                      (row.pl ?? row.profitLoss ?? 0) >= 0
                        ? "pl-positive"
                        : "pl-negative"
                    }
                  >
                    {row.pl ?? row.profitLoss ?? row.pnl ?? "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default UserProfitLossTab;
